import { useEffect, useMemo, useRef, useState } from 'react';
import { Paperclip, Send, X } from 'lucide-react';
import type { CommentAttachment } from '../../../lib/tasksApi';
import { uploadTaskFile } from '../../../lib/tasksApi';
import type { UploadProgressHandler } from '../../../lib/fileUpload';
import { useUsers } from '../../../hooks/useUsers';
import { useUploadQueue } from '../../../hooks/useUploadQueue';
import { UploadChipList } from '../../ui/UploadProgress';

interface Props {
  /** Uploads are keyed on the task (POST /api/tasks/:id/upload-url). */
  taskId?: string;
  /**
   * Where attachments go when the box is not attached to a task — the bug
   * reports view passes its own uploader. Defaults to the task endpoint, so
   * every existing caller keeps working by passing `taskId` alone.
   */
  uploadFile?: (file: File, onProgress: UploadProgressHandler) => Promise<CommentAttachment>;
  /** Text and files to start from (editing an existing comment). */
  initialValue?: string;
  initialAttachments?: CommentAttachment[];
  placeholder?: string;
  submitLabel?: string;
  busyLabel?: string;
  busy?: boolean;
  autoFocus?: boolean;
  /** Optional cancel action (editing); rendered next to the send button. */
  onCancel?: () => void;
  cancelLabel?: string;
  /** Resolves when saved; the box clears itself unless `keepAfterSubmit`. */
  onSubmit: (content: string, attachments: CommentAttachment[]) => Promise<unknown> | void;
  keepAfterSubmit?: boolean;
  /** Tighter paddings for use inside a list row. */
  compact?: boolean;
  /** Visible lines before the user resizes it (default 2). */
  rows?: number;
  className?: string;
}

/**
 * The "add a comment" box from the task detail page, as a reusable piece:
 * markdown text with @mentions, photo/file attachments via the paperclip
 * button, drag-and-drop or paste, and ⌘/Ctrl+Enter to send. The task page and
 * the daily status board both render this one component.
 */
export function CommentComposerBox({
  taskId,
  uploadFile,
  initialValue = '',
  initialAttachments = [],
  placeholder = 'Lasă un comentariu…',
  submitLabel = 'Comentează',
  busyLabel = 'Se trimite…',
  busy = false,
  autoFocus = false,
  onCancel,
  cancelLabel = 'Renunță',
  onSubmit,
  keepAfterSubmit = false,
  compact = false,
  rows,
  className = '',
}: Props) {
  const { data: users } = useUsers();
  const [value, setValue] = useState(initialValue);
  const [pending, setPending] = useState<CommentAttachment[]>(initialAttachments);
  // One chip per in-flight file, each with its own bar — picking three files
  // used to give one spinner and no way to tell what was still going.
  const { uploads, isUploading, run } = useUploadQueue();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus();
  }, [autoFocus]);

  const suggestions = useMemo(() => {
    if (mentionQuery == null || !users) return [];
    return users.filter((u) => u.name.toLowerCase().includes(mentionQuery)).slice(0, 6);
  }, [mentionQuery, users]);

  function onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value;
    setValue(text);
    const caret = e.target.selectionStart ?? text.length;
    const match = /@([\w.-]*)$/.exec(text.slice(0, caret));
    setMentionQuery(match ? match[1].toLowerCase() : null);
  }

  function applyMention(name: string) {
    const el = textareaRef.current;
    const caret = el?.selectionStart ?? value.length;
    const before = value.slice(0, caret).replace(/@([\w.-]*)$/, `@${name} `);
    setValue(before + value.slice(caret));
    setMentionQuery(null);
    el?.focus();
  }

  async function handleFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    setUploadError(null);
    try {
      const upload =
        uploadFile ??
        ((file: File, onProgress: UploadProgressHandler) =>
          uploadTaskFile(taskId as string, file, onProgress));
      for (const f of list) {
        const uploaded = await run(f, (onProgress) => upload(f, onProgress));
        // Chip by chip, so a finished file is attached and visible while the
        // rest of the batch is still uploading.
        setPending((prev) => [...prev, uploaded]);
      }
    } catch (err) {
      // A failed upload used to be swallowed: the spinner stopped, no chip
      // appeared and nothing said why. Show the reason instead.
      setUploadError(err instanceof Error ? err.message : 'Încărcarea fișierului a eșuat.');
    }
  }

  async function submit() {
    const content = value.trim();
    if (!content && pending.length === 0) return;
    await onSubmit(content, pending);
    if (!keepAfterSubmit) {
      setValue('');
      setPending([]);
      setMentionQuery(null);
    }
  }

  const canSubmit = !busy && !isUploading && (value.trim().length > 0 || pending.length > 0);

  return (
    <div
      className={`relative flex-1 rounded-md border border-gray-200 bg-card ${className}`}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
      }}
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        // Paste attaches ANY file, not just images (BUG-7). The file button and
        // drag-and-drop above already accept anything `handleFiles` can upload;
        // paste alone filtered on `image/`, so copying a PDF next to two JPEGs
        // attached only the JPEGs and dropped the PDF — or, for a lone PDF,
        // pasted its filename as text. `clipboardData.files` is the whole set.
        onPaste={(e) => {
          const files = Array.from(e.clipboardData.files);
          if (files.length) {
            e.preventDefault();
            void handleFiles(files);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void submit();
          }
          if (e.key === 'Escape' && onCancel) onCancel();
        }}
        rows={rows ?? (compact ? 1 : 2)}
        placeholder={placeholder}
        className={`block w-full resize-y bg-transparent px-3 text-[13px] leading-relaxed text-gray-800 placeholder:text-gray-400 focus:outline-none ${
          compact ? 'py-1.5' : 'py-2'
        }`}
      />

      {uploadError && (
        <p className="px-3 pb-2 text-[11px] leading-relaxed text-red-500">{uploadError}</p>
      )}

      {(pending.length > 0 || uploads.length > 0) && (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
          <UploadChipList uploads={uploads} />
          {pending.map((a, i) => (
            <span
              key={`${a.name}-${i}`}
              className="inline-flex items-center gap-1 rounded border border-gray-200 px-1.5 py-0.5 text-[11px] text-gray-500"
            >
              <Paperclip size={10} /> {a.name}
              <button
                type="button"
                onClick={() => setPending((p) => p.filter((_, idx) => idx !== i))}
                className="text-gray-400 hover:text-red-500"
                aria-label="Elimină atașamentul"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <ul className="absolute bottom-full left-2 z-10 mb-1 max-h-44 w-56 overflow-auto rounded-md border border-gray-200 bg-gray-200 py-1 shadow-lg">
          {suggestions.map((u) => (
            <li key={u.id}>
              <button
                type="button"
                onClick={() => applyMention(u.name)}
                className="flex w-full items-center px-3 py-1.5 text-left text-[13px] text-gray-700 hover:bg-gray-200"
              >
                {u.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between border-t border-gray-200 px-2 py-1.5">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-1 rounded px-1.5 py-1 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
          title="Atașează o fotografie sau un fișier"
        >
          <Paperclip size={14} />
        </button>
        <input
          ref={fileRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded px-2 py-1 text-[12px] text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            >
              {cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={!canSubmit}
            className="inline-flex items-center gap-1.5 rounded bg-brand-600 px-2.5 py-1 text-[12px] font-medium text-white hover:bg-brand-700 disabled:opacity-40"
          >
            <Send size={12} /> {busy ? busyLabel : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
